import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { logoUrl } from "@/lib/api";

/**
 * Animation d'ouverture RANNEA (~5 s) — vectorielle SVG + CSS.
 * 1. Une portée musicale (barre de reprise + 5 lignes) se dessine de gauche à droite.
 * 2. Les lignes se déplacent, se courbent et se redistribuent en zigzags
 *    évoquant la construction des lettres ; la barre de reprise devient la
 *    hampe du R, ses points forment la tête de note du logo.
 * 3. Le logo officiel RANNEA apparaît en fondu croisé (jamais déformé).
 * 4. Le logo du groupe (s'il existe) apparaît en fondu, secondaire, ratio préservé.
 * Pendant l'animation, la session et les données du groupe chargent en parallèle.
 */
export default function RanneaIntro({ onDone }) {
  const { group } = useAuth();
  const [fading, setFading] = useState(false);
  const groupLogo = group?.logo_path ? logoUrl() : null;

  useEffect(() => {
    const t1 = setTimeout(() => setFading(true), 5800);
    const t2 = setTimeout(onDone, 6400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[100] app-bg flex items-center justify-center transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`}
      onClick={onDone}
      data-testid="rannea-intro"
    >
      <div className="relative w-[88vw] max-w-2xl">
        <svg viewBox="0 0 1200 400" className="w-full h-auto rannea-stage" data-testid="rannea-intro-svg" aria-hidden="true">
          {/* Barre de reprise */}
          <rect className="rb-thick" x="132" y="126" width="13" height="128" rx="6" />
          <rect className="rb-thin" x="158" y="126" width="4" height="128" rx="2" />
          <circle className="rb-dot rd1" cx="180" cy="158" r="8" />
          <circle className="rb-dot rd2" cx="180" cy="222" r="8" />
          {/* Portée : 5 lignes */}
          <path className="staff rl1" d="M120,140 L1080,140" />
          <path className="staff rl2" d="M120,165 L1080,165" />
          <path className="staff rl3" d="M120,190 L1080,190" />
          <path className="staff rl4" d="M120,215 L1080,215" />
          <path className="staff rl5" d="M120,240 L1080,240" />
        </svg>
        <img
          src="/rannea-logo-white.png"
          alt="RANNEA — The link between musicians"
          className="rannea-final-logo absolute inset-0 m-auto w-full"
          data-testid="rannea-intro-logo"
        />
      </div>
      {groupLogo && (
        <div className="absolute inset-x-0 bottom-[9vh] flex justify-center pointer-events-none">
          <img
            src={groupLogo}
            alt={group?.name || "Logo du groupe"}
            className="rannea-group-logo max-h-[20vh] max-w-[60vw] sm:max-w-[300px] object-contain mix-blend-screen rounded-2xl"
            onError={(e) => e.currentTarget.remove()}
            data-testid="rannea-intro-group-logo"
          />
        </div>
      )}
    </div>
  );
}
